import { useState, useEffect } from 'react';
import { X, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { getColaboradores, addColaboradorToCirugia, getEspecialidades } from '../../services/cirugiasService';
import SearchableSelect from '../common/SearchableSelect';

interface Props {
  cirugiaId: number;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AgregarColaboradorModal({ cirugiaId, onClose, onSuccess }: Props) {
  const [loading, setLoading] = useState(false);
  const [colaboradores, setColaboradores] = useState<any[]>([]);
  const [especialidadesMap, setEspecialidadesMap] = useState<Record<number, string>>({});
  const [formData, setFormData] = useState({
    colaboradorId: 0,
    rolEnCirugia: '',
    confirmado: false,
    notas: '',
  });

  useEffect(() => {
    loadCatalogos();
  }, []);

  const loadCatalogos = async () => {
    try {
      const [colaboradoresRes, especialidadesRes] = await Promise.all([
        getColaboradores({ isActive: true, take: 500 }),
        getEspecialidades({ isActive: true, take: 500 }),
      ]);

      const colaboradoresList = Array.isArray(colaboradoresRes)
        ? colaboradoresRes
        : Array.isArray(colaboradoresRes?.data)
        ? colaboradoresRes.data
        : (colaboradoresRes?.data?.data || []);

      const especialidadesList = Array.isArray(especialidadesRes)
        ? especialidadesRes
        : Array.isArray(especialidadesRes?.data)
        ? especialidadesRes.data
        : (especialidadesRes?.data?.data || []);

      const map: Record<number, string> = {};
      especialidadesList.forEach((esp: any) => {
        if (esp?.id) {
          map[esp.id] = esp.nombre;
        }
      });

      setColaboradores(colaboradoresList);
      setEspecialidadesMap(map);
    } catch (error) {
      console.error('Error al cargar colaboradores o especialidades:', error);
      toast.error('Error al cargar catálogos de colaboradores');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.colaboradorId) {
      toast.error('Debe seleccionar un colaborador');
      return;
    }

    if (!formData.rolEnCirugia) {
      toast.error('Debe especificar el rol en la cirugía');
      return;
    }

    try {
      setLoading(true);
      await addColaboradorToCirugia(cirugiaId, formData);
      toast.success('Colaborador agregado exitosamente');
      onSuccess();
    } catch (error: any) {
      console.error('Error al agregar colaborador:', error);
      toast.error(error.response?.data?.message || 'Error al agregar colaborador');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserPlus className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Agregar Colaborador</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <SearchableSelect
            label="Colaborador"
            required
            value={formData.colaboradorId}
            onChange={(value) => {
              const selected = colaboradores.find((c) => c.id === value);
              const especialidadNombre = selected && especialidadesMap[selected.especialidadId]
                ? especialidadesMap[selected.especialidadId]
                : selected?.tipo || '';

              setFormData((prev) => ({
                ...prev,
                colaboradorId: value,
                rolEnCirugia: especialidadNombre || prev.rolEnCirugia,
              }));
            }}
            options={Array.isArray(colaboradores) ? colaboradores.map((c) => ({
              value: c.id,
              label: `${c.nombre} ${c.apellidos}`,
              subtitle: `${especialidadesMap[c.especialidadId] || 'Sin especialidad'} · ${c.tipo}`,
            })) : []}
            placeholder="Buscar colaborador por nombre..."
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rol en la Cirugía <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.rolEnCirugia}
              onChange={(e) => setFormData((prev) => ({ ...prev, rolEnCirugia: e.target.value }))}
              placeholder="Se autollenará con la especialidad del colaborador seleccionado"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.confirmado}
              onChange={(e) => setFormData((prev) => ({ ...prev, confirmado: e.target.checked }))}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label className="ml-2 text-sm font-medium text-gray-700">
              Confirmado
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas
            </label>
            <textarea
              value={formData.notas}
              onChange={(e) => setFormData((prev) => ({ ...prev, notas: e.target.value }))}
              rows={3}
              placeholder="Notas adicionales sobre la participación del colaborador..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <UserPlus className="w-5 h-5" />
              {loading ? 'Agregando...' : 'Agregar Colaborador'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
